import { useEffect, useRef } from 'react';
import { Outlet, useNavigation } from 'react-router-dom';
import LoadingBar, { type LoadingBarRef } from 'react-top-loading-bar';
import { useLocalStorage, useMediaQuery } from 'usehooks-ts';

import { SidebarInset, SidebarProvider } from '@kognitos/lattice';
import { AppErrorBoundary } from '@/modules/sentry';
import { useAuth } from '@/shared/auth';
import { LowCreditsAlert } from '@/shared/components/LowCreditsAlert';
import { STORAGE_KEYS } from '@/shared/constants';
import { NudgeManager, useCreateFirstDraftNudge } from '@/shared/tours/nudges';

import { useIsOnboardingOrInvitationPage } from '@/routes/util';
import { useSyncUrlParamsToStore } from '@/shared/hooks/useSyncUrlParamsToStore';
import {
  selectCurrentOrganizationId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { AppSidebar } from './AppSidebar';
import { ConnectOnOAuthReturn } from '@/modules/integrations/components/ConnectOnOAuthReturn';

import '@/modules/automation/styles.css';
import noop from 'lodash/noop';

const Layout = () => {
  useSyncUrlParamsToStore();
  useCreateFirstDraftNudge();
  const { isAuthenticated } = useAuth();
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const navigation = useNavigation();
  const loadingBarRef = useRef<LoadingBarRef>(null);
  const isOnboardingOrInvitationPage = useIsOnboardingOrInvitationPage(); // Detect onboarding to keep sidebar collapsed

  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    STORAGE_KEYS.SIDEBAR_COLLAPSED,
    true,
  );

  // At/below 1024px the sidebar auto-collapses to the icon rail so content
  // (e.g. the Guidance Center panels) gets the full width; the user's manual
  // preference still applies above 1024px.
  const isBelowLg = useMediaQuery('(max-width: 1024px)');

  const handleSidebarChange = (open: boolean) => {
    setSidebarCollapsed(!open);
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Sync navigation state to loading bar imperative API
  useEffect(() => {
    if (navigation.state === 'loading') {
      loadingBarRef.current?.continuousStart();
    } else {
      loadingBarRef.current?.complete();
    }
  }, [navigation.state]);

  return (
    <>
      <LoadingBar ref={loadingBarRef} height={3} color="var(--primary)" />
      <SidebarProvider
        open={
          isOnboardingOrInvitationPage || isBelowLg ? false : !sidebarCollapsed
        } // Force closed during onboarding/invitation and below lg
        onOpenChange={isOnboardingOrInvitationPage ? noop : handleSidebarChange} // Ignore toggle attempts during onboarding/invitation
      >
        {isAuthenticated && <AppSidebar />}
        {/*
         * min-w-0 lets the content area shrink below its intrinsic content
         * width instead of overflowing past the sidebar. Without it, a flex
         * child with wide content (e.g. the Guidance Center AOP editor) forces
         * the inset wider than the viewport, pushing right-aligned content like
         * the Resolution Agent chat off-screen (ENG-4109).
         */}
        <SidebarInset className="min-w-0">
          <div className="flex flex-col min-h-screen">
            {isAuthenticated && <LowCreditsAlert key={organizationId} />}
            {isAuthenticated && <ConnectOnOAuthReturn />}
            <main className="block flex-1">
              <AppErrorBoundary>
                <Outlet />
              </AppErrorBoundary>
            </main>
          </div>
        </SidebarInset>
        {isAuthenticated && <NudgeManager />}
      </SidebarProvider>
    </>
  );
};

export default Layout;
